import { blogPosts as initialCoreBlogPosts, BLOG_DEFAULT_IMAGE } from "./blogPosts";

const CUSTOM_POSTS_STORAGE_KEY = "clubhosty_custom_posts_v2";
const DRAFTS_STORAGE_KEY = "clubhosty_blog_drafts_v2";
const DELETED_POSTS_STORAGE_KEY = "clubhosty_deleted_posts_v2";
const CHANGE_EVENT_NAME = "clubhosty_blogs_updated";

// Helper to safely access localStorage
const safeStorage = {
  getItem: (key) => {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn("Storage write failed:", e);
    }
  },
  removeItem: (key) => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn("Storage remove failed:", e);
    }
  },
};

// Parse JSON safely
const parseJson = (str, fallback = []) => {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// Format slug to kebab-case
export const formatSlug = (text = "") => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Estimate read time from word count
export const calculateReadTime = (content) => {
  if (!content) return "3 min read";
  let totalWords = 0;

  if (typeof content === "string") {
    totalWords = content.trim().split(/\s+/).length;
  } else if (Array.isArray(content)) {
    content.forEach((sec) => {
      if (sec.heading) totalWords += sec.heading.split(/\s+/).length;
      if (Array.isArray(sec.paragraphs)) {
        sec.paragraphs.forEach((p) => {
          totalWords += p.split(/\s+/).length;
        });
      }
      if (Array.isArray(sec.list)) {
        sec.list.forEach((li) => {
          totalWords += li.split(/\s+/).length;
        });
      }
      if (sec.rawContent) {
        totalWords += sec.rawContent.split(/\s+/).length;
      }
    });
  }

  const minutes = Math.max(1, Math.ceil(totalWords / 200));
  return `${minutes} min read`;
};

// Dispatch change event to keep UI sync across tabs/components
const notifyBlogChange = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT_NAME));
  }
};

/**
 * Get all custom/overridden posts from localStorage
 */
export const getCustomPosts = () => {
  return parseJson(safeStorage.getItem(CUSTOM_POSTS_STORAGE_KEY), []);
};

/**
 * Get all draft posts from localStorage
 */
export const getDraftPosts = () => {
  return parseJson(safeStorage.getItem(DRAFTS_STORAGE_KEY), []);
};

/**
 * Get slugs of deleted core posts
 */
export const getDeletedSlugs = () => {
  return parseJson(safeStorage.getItem(DELETED_POSTS_STORAGE_KEY), []);
};

/**
 * Check if a slug belongs to a core (initial) post
 */
export const isCorePost = (slug) => {
  return initialCoreBlogPosts.some((p) => p.slug === slug);
};

/**
 * Returns a list of all blog posts merged:
 * 1. Default core posts (42+ items)
 * 2. Overridden/edited versions of core posts
 * 3. Newly created custom posts
 * 4. Filters out deleted posts
 * 5. Optionally includes drafts
 */
export const getAllBlogPosts = ({ includeDrafts = false } = {}) => {
  const customPosts = getCustomPosts();
  const deletedSlugs = getDeletedSlugs();
  const drafts = includeDrafts ? getDraftPosts() : [];

  // Map of custom overrides by slug
  const customMap = new Map();
  customPosts.forEach((post) => {
    if (post && post.slug) {
      customMap.set(post.slug, post);
    }
  });

  // Base list starting with initial core posts
  const resultList = [];
  const processedSlugs = new Set();

  initialCoreBlogPosts.forEach((corePost) => {
    if (deletedSlugs.includes(corePost.slug)) return; // Skipped if deleted

    processedSlugs.add(corePost.slug);

    if (customMap.has(corePost.slug)) {
      // Use edited version
      resultList.push({
        ...corePost,
        ...customMap.get(corePost.slug),
        image: customMap.get(corePost.slug).image || corePost.image || BLOG_DEFAULT_IMAGE,
        isCore: true,
        isEdited: true,
      });
    } else {
      // Use original core post
      resultList.push({
        ...corePost,
        image: corePost.image || BLOG_DEFAULT_IMAGE,
        isCore: true,
        isEdited: false,
      });
    }
  });

  // Add new custom posts that are not overrides of core posts
  customPosts.forEach((post) => {
    if (!processedSlugs.has(post.slug) && !deletedSlugs.includes(post.slug)) {
      processedSlugs.add(post.slug);
      resultList.push({
        ...post,
        image: post.image || BLOG_DEFAULT_IMAGE,
        isCore: false,
        isCustom: true,
      });
    }
  });

  // Add drafts if requested
  if (includeDrafts) {
    drafts.forEach((draft) => {
      resultList.push({
        ...draft,
        image: draft.image || BLOG_DEFAULT_IMAGE,
        isDraft: true,
      });
    });
  }

  // Sort by date (newest first)
  return resultList.sort((a, b) => {
    const timeA = new Date(a.date).getTime() || 0;
    const timeB = new Date(b.date).getTime() || 0;
    return timeB - timeA;
  });
};

/**
 * Fetch a single post by slug (checks custom/edited, core, and drafts)
 */
export const getBlogPostBySlug = (slug) => {
  if (!slug) return null;

  const deletedSlugs = getDeletedSlugs();
  if (deletedSlugs.includes(slug)) return null;

  // 1. Check custom posts / overrides
  const customPosts = getCustomPosts();
  const customMatch = customPosts.find((p) => p.slug === slug);

  // 2. Check core posts
  const coreMatch = initialCoreBlogPosts.find((p) => p.slug === slug);

  if (customMatch && coreMatch) {
    return {
      ...coreMatch,
      ...customMatch,
      image: customMatch.image || coreMatch.image || BLOG_DEFAULT_IMAGE,
      isCore: true,
      isEdited: true,
    };
  }

  if (customMatch) {
    return {
      ...customMatch,
      image: customMatch.image || BLOG_DEFAULT_IMAGE,
      isCore: false,
      isCustom: true,
    };
  }

  if (coreMatch) {
    return {
      ...coreMatch,
      image: coreMatch.image || BLOG_DEFAULT_IMAGE,
      isCore: true,
      isEdited: false,
    };
  }

  // 3. Fallback check drafts
  const drafts = getDraftPosts();
  const draftMatch = drafts.find((d) => d.slug === slug);
  if (draftMatch) {
    return {
      ...draftMatch,
      image: draftMatch.image || BLOG_DEFAULT_IMAGE,
      isDraft: true,
    };
  }

  return null;
};

/**
 * Save or publish a blog post
 */
export const saveBlogPost = (postData, { isDraft = false } = {}) => {
  if (!postData || !postData.title) {
    throw new Error("Post title is required.");
  }

  const slug = formatSlug(postData.slug || postData.title);
  if (!slug) {
    throw new Error("Valid slug is required.");
  }

  const cleanPost = {
    ...postData,
    slug,
    readTime: postData.readTime || calculateReadTime(postData.content || postData.rawContent),
    date: postData.date || new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    image: postData.image?.trim() || BLOG_DEFAULT_IMAGE,
    updatedAt: new Date().toISOString(),
  };

  if (isDraft) {
    const drafts = getDraftPosts();
    const index = drafts.findIndex((d) => d.slug === slug);
    if (index >= 0) {
      drafts[index] = cleanPost;
    } else {
      drafts.unshift(cleanPost);
    }
    safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
  } else {
    // If it was in drafts, remove from drafts
    const drafts = getDraftPosts().filter((d) => d.slug !== slug);
    safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));

    // Save into custom posts
    const customPosts = getCustomPosts();
    const index = customPosts.findIndex((p) => p.slug === slug);
    if (index >= 0) {
      customPosts[index] = cleanPost;
    } else {
      customPosts.unshift(cleanPost);
    }
    safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

    // Unmark from deleted if it was previously deleted
    const deletedSlugs = getDeletedSlugs().filter((s) => s !== slug);
    safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));
  }

  notifyBlogChange();
  return cleanPost;
};

/**
 * Delete a post (custom or core)
 */
export const deleteBlogPost = (slug) => {
  if (!slug) return;

  // Remove from custom posts
  const customPosts = getCustomPosts().filter((p) => p.slug !== slug);
  safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

  // Remove from drafts
  const drafts = getDraftPosts().filter((d) => d.slug !== slug);
  safeStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));

  // If it's a core post, add to deletedSlugs
  if (isCorePost(slug)) {
    const deletedSlugs = getDeletedSlugs();
    if (!deletedSlugs.includes(slug)) {
      deletedSlugs.push(slug);
      safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));
    }
  }

  notifyBlogChange();
};

/**
 * Reset an edited core post back to its default code definition
 */
export const restoreCorePost = (slug) => {
  if (!slug) return;

  const customPosts = getCustomPosts().filter((p) => p.slug !== slug);
  safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(customPosts));

  const deletedSlugs = getDeletedSlugs().filter((s) => s !== slug);
  safeStorage.setItem(DELETED_POSTS_STORAGE_KEY, JSON.stringify(deletedSlugs));

  notifyBlogChange();
};

/**
 * Returns latest blog post for header / banner
 */
export const getLatestBlogPost = () => {
  const all = getAllBlogPosts({ includeDrafts: false });
  return all.length > 0 ? all[0] : null;
};

/**
 * Subscribe to blog data changes
 */
export const subscribeToBlogChanges = (callback) => {
  if (typeof window === "undefined") return () => {};

  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT_NAME, handler);
  window.addEventListener("storage", handler);

  return () => {
    window.removeEventListener(CHANGE_EVENT_NAME, handler);
    window.removeEventListener("storage", handler);
  };
};

/**
 * Export all posts data as JSON string for backup/sharing
 */
export const exportAllPostsAsJson = () => {
  const allPosts = getAllBlogPosts({ includeDrafts: true });
  return JSON.stringify(allPosts, null, 2);
};

/**
 * Import posts from JSON string
 */
export const importPostsFromJson = (jsonString) => {
  try {
    const posts = JSON.parse(jsonString);
    if (!Array.isArray(posts)) {
      throw new Error("Invalid format: expected an array of blog posts.");
    }

    const currentCustom = getCustomPosts();
    const map = new Map();
    currentCustom.forEach((p) => map.set(p.slug, p));

    posts.forEach((p) => {
      if (p && p.slug && p.title) {
        map.set(p.slug, p);
      }
    });

    const merged = Array.from(map.values());
    safeStorage.setItem(CUSTOM_POSTS_STORAGE_KEY, JSON.stringify(merged));
    notifyBlogChange();
    return { success: true, count: posts.length };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

/**
 * Generate formatted ES6 JS code for a blog post
 * Matches exact structure of src/Blog/posts/*.js
 */
export const generateJsPostModule = (post) => {
  const varName = post.slug
    .replace(/-([a-z0-9])/g, (g) => g[1].toUpperCase())
    .replace(/^[0-9]/, "_$&") + "Post";

  return `const ${varName} = ${JSON.stringify(post, null, 2)};\n\nexport default ${varName};\n`;
};
